import { lsGet, lsRemove, lsSet } from '@/utils/storage'

export type ThemeColorProfile =
  | 'blue'
  | 'green'
  | 'teal'
  | 'violet'
  | 'rose'
  | 'amber'

export interface ThemeColorProfileOption {
  value: ThemeColorProfile
  label: string
  description: string
  hue: number
}

const COLOR_OVERRIDE_STORAGE_KEY = 'color-profile-override'
const COLOR_ATTRIBUTE = 'data-color'

export const THEME_COLOR_PROFILE_OPTIONS: ThemeColorProfileOption[] = [
  { value: 'blue', label: 'Blue', description: 'Clean and energetic', hue: 234 },
  { value: 'green', label: 'Green', description: 'Fresh and active', hue: 134 },
  { value: 'teal', label: 'Teal', description: 'Calm and balanced', hue: 190 },
  { value: 'violet', label: 'Violet', description: 'Bright and focused', hue: 270 },
  { value: 'rose', label: 'Rose', description: 'Warm and expressive', hue: 350 },
  { value: 'amber', label: 'Amber', description: 'Soft and grounded', hue: 75 },
]

function isThemeColorProfile(value: string): value is ThemeColorProfile {
  return THEME_COLOR_PROFILE_OPTIONS.some((option) => option.value === value)
}

export function normalizeThemeColorProfile(value?: string | null): ThemeColorProfile | null {
  if (!value) {
    return null
  }

  return isThemeColorProfile(value) ? value : null
}

function setDocumentColorAttribute(attribute: string, profile?: ThemeColorProfile | null) {
  if (typeof document === 'undefined') {
    return
  }

  if (!profile) {
    document.documentElement.removeAttribute(attribute)
    return
  }

  document.documentElement.setAttribute(attribute, profile)
}

export function getThemeColorProfileOverride(): ThemeColorProfile | null {
  return normalizeThemeColorProfile(lsGet(COLOR_OVERRIDE_STORAGE_KEY))
}

export function getAppliedThemeColorProfile(): ThemeColorProfile | null {
  if (typeof document === 'undefined') {
    return null
  }

  return normalizeThemeColorProfile(
    document.documentElement.getAttribute(COLOR_ATTRIBUTE),
  )
}

export function setThemeColorProfile(profile?: ThemeColorProfile | null) {
  setDocumentColorAttribute(COLOR_ATTRIBUTE, profile)
}

export function applyThemeColorProfileOverride() {
  setThemeColorProfile(getThemeColorProfileOverride())
}

export function setThemeColorProfileOverride(profile?: ThemeColorProfile | null) {
  if (profile) {
    lsSet(COLOR_OVERRIDE_STORAGE_KEY, profile)
    setThemeColorProfile(profile)
    return
  }

  lsRemove(COLOR_OVERRIDE_STORAGE_KEY)
  setThemeColorProfile(null)
}
